import {
    ChangeEvent,
    Fragment,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import clsx from 'clsx'
import Section from '../../components/Section'
import helixSearchV2Api from '../../api/helixSearchV2/HelixSearchV2Api'
import {
    GetHelixSearchQueueRes,
    HelixSearchQueueItemDto,
    HelixSearchQueueLogDto,
} from '../../api/helixSearchV2/models/HelixSearchQueue'
import {
    HelixSearchLogStatusV2,
    HelixSearchSessionStatusV2,
} from '../../api/helixSearchV2/models/StartHelixSearchV2'
import LoadingSpinner from '../../uiV2/common/loading/LoadingSpinner'
import Button from '../../uiV2/common/form/inputs/Button'
import CheckboxInput from '../../uiV2/common/form/inputs/CheckboxInput'
import Modal from '../../uiV2/common/modals/Modal'
import { toast } from 'sonner'
import { Pages } from '../../Pages'
import { useNavigate } from 'react-router-dom'
import { formatDurationMs } from '../../utils/dateUtils'
import {
    HelixSearchLogStatusBadge,
    HelixSearchSessionStatusBadge,
} from '../../uiV2/helixSearchV2/HelixSearchBadges'

const STATUS_OPTIONS: Array<{
    value: HelixSearchSessionStatusV2
    label: string
}> = [
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'CANCELLING', label: 'Cancelling' },
    { value: 'CANCELED', label: 'Canceled' },
    { value: 'COMPLETE', label: 'Complete' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'EXPIRED', label: 'Expired' },
]

const LOG_TERMINAL_STATUSES = new Set<HelixSearchLogStatusV2>([
    'FAILED',
    'COMPLETE',
    'CANCELED',
    'EXPIRED',
])

const PER_PAGE_DEFAULT = 25
const AUTO_REFRESH_INTERVAL_MS = 5_000
const PURGE_CONFIRMATION_TEXT = 'I understand'
const HELIX_TABLE_COLUMN_COUNT = 15
const HELIX_TABLE_CLASS =
    'main-table table-fixed w-full text-[11px] leading-tight break-words'
const HELIX_NESTED_TABLE_CLASS =
    'nested-table table-fixed w-full text-[11px] break-words'
const HELIX_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const HELIX_CELL_CLASS = 'px-2 py-1 align-top min-w-0'

type ExpandableTextBoxProps = {
    text: string | null | undefined
    maxLines?: number
    className?: string
    contentClassName?: string
}

type ManualFailTarget = {
    sst: string
    tenantId: string
    logId: string
    currentReceiptHandle: string | null
}

function ExpandableTextBox({
    text,
    maxLines = 5,
    className,
    contentClassName,
}: ExpandableTextBoxProps): React.JSX.Element {
    const [expanded, setExpanded] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setExpanded(false)
    }, [text])

    useEffect(() => {
        if (!text) {
            setIsOverflowing(false)
            return
        }

        if (expanded) {
            return
        }

        const el = textRef.current
        if (!el) {
            return
        }

        const frameId = window.requestAnimationFrame(() => {
            setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
        })

        return () => {
            window.cancelAnimationFrame(frameId)
        }
    }, [text, expanded, maxLines])

    if (!text) {
        return <span>—</span>
    }

    return (
        <div
            className={clsx(
                'rounded border border-gray-200 bg-white/80 p-2',
                className,
            )}
        >
            <div
                ref={textRef}
                className={clsx(
                    'font-mono text-[11px] leading-snug text-gray-800',
                    !expanded && `line-clamp-${maxLines} overflow-hidden`,
                    contentClassName,
                )}
            >
                {text}
            </div>
            {isOverflowing ? (
                <button
                    type="button"
                    className="mt-1 text-xs font-semibold text-blue-600 underline"
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            ) : null}
        </div>
    )
}

const formatDateTimeParts = (value: string): { date: string; time: string } => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return { date: value, time: '' }
    }

    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]

    const pad = (num: number) => num.toString().padStart(2, '0')

    const datePart = `${pad(date.getDate())} ${
        months[date.getMonth()]
    } ${date.getFullYear()}`
    const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds(),
    )}`

    return { date: datePart, time: timePart }
}

const renderDateTime = (value: string | null | undefined): ReactNode => {
    if (!value) {
        return '—'
    }

    const parts = formatDateTimeParts(value)
    return (
        <>
            <div>{parts.date}</div>
            <div className="text-[10px] text-gray-600">{parts.time}</div>
        </>
    )
}

const formatJsonPayload = (value: string | null): string => {
    if (!value) return ''
    try {
        return JSON.stringify(JSON.parse(value), null, 2)
    } catch {
        return value
    }
}

const formatCount = (value?: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '—'
    }
    return value.toLocaleString()
}

export default function HelixSearchQueue(): React.JSX.Element {
    const [pageNumber, setPageNumber] = useState(1)
    const [selectedStatuses, setSelectedStatuses] = useState<
        HelixSearchSessionStatusV2[]
    >([])
    const [refreshToken, setRefreshToken] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [items, setItems] = useState<HelixSearchQueueItemDto[]>([])
    const [queueMeta, setQueueMeta] = useState<
        Pick<GetHelixSearchQueueRes, 'total' | 'hasMore' | 'perPage'>
    >({ total: 0, hasMore: false, perPage: PER_PAGE_DEFAULT })
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(
        {},
    )
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [showPurgeModal, setShowPurgeModal] = useState(false)
    const [purgeConfirmation, setPurgeConfirmation] = useState('')
    const [purgeLoading, setPurgeLoading] = useState(false)
    const [purgeError, setPurgeError] = useState<string | undefined>()
    const [manualFailTarget, setManualFailTarget] = useState<
        ManualFailTarget | undefined
    >(undefined)
    const [manualFailReceiptHandle, setManualFailReceiptHandle] = useState('')
    const [manualFailSetSessionToFailed, setManualFailSetSessionToFailed] =
        useState(true)
    const [manualFailLoading, setManualFailLoading] = useState(false)
    const [manualFailError, setManualFailError] = useState<string | undefined>()
    const loadingRef = useRef(false)
    const navigate = useNavigate()

    const appliedStatuses = useMemo(
        () => (selectedStatuses.length > 0 ? selectedStatuses : undefined),
        [selectedStatuses],
    )

    const purgeConfirmationMatches =
        purgeConfirmation === PURGE_CONFIRMATION_TEXT

    useEffect(() => {
        let ignore = false
        const run = async () => {
            setLoading(true)
            loadingRef.current = true
            setError(undefined)
            try {
                const res = await helixSearchV2Api.getQueue({
                    pageNumber,
                    perPage: PER_PAGE_DEFAULT,
                    statuses: appliedStatuses,
                })
                if (ignore) return
                setItems(res.items)
                setQueueMeta({
                    total: res.total,
                    hasMore: res.hasMore,
                    perPage: res.perPage ?? PER_PAGE_DEFAULT,
                })
                setExpandedRows((prev) => {
                    const next: Record<string, boolean> = {}
                    for (const item of res.items) {
                        if (prev[item.sst]) {
                            next[item.sst] = true
                        }
                    }
                    return next
                })
            } catch (err) {
                if (ignore) return
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Failed to load Helix search queue'
                setError(message)
            } finally {
                if (!ignore) {
                    setLoading(false)
                    loadingRef.current = false
                }
            }
        }

        void run()

        return () => {
            loadingRef.current = false
            ignore = true
        }
    }, [pageNumber, appliedStatuses, refreshToken])

    useEffect(() => {
        if (!autoRefresh) {
            return
        }

        const intervalId = window.setInterval(() => {
            if (!loadingRef.current) {
                setRefreshToken((token) => token + 1)
            }
        }, AUTO_REFRESH_INTERVAL_MS)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [autoRefresh])

    const handleToggleStatus = (
        status: HelixSearchSessionStatusV2,
        checked: boolean,
    ) => {
        setSelectedStatuses((prev) => {
            if (checked) {
                if (prev.includes(status)) {
                    return prev
                }
                return [...prev, status]
            }
            return prev.filter((value) => value !== status)
        })
        setPageNumber(1)
    }

    const handleAutoRefreshChange = (checked: boolean) => {
        setAutoRefresh(checked)
    }

    const handleOpenPurgeModal = () => {
        setShowPurgeModal(true)
        setPurgeConfirmation('')
        setPurgeError(undefined)
    }

    const handleClosePurgeModal = () => {
        if (purgeLoading) {
            return
        }
        setShowPurgeModal(false)
        setPurgeConfirmation('')
        setPurgeError(undefined)
    }

    const handlePurgeConfirmationChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        setPurgeConfirmation(event.target.value)
        if (purgeError) {
            setPurgeError(undefined)
        }
    }

    const handleConfirmPurge = async () => {
        if (purgeLoading || !purgeConfirmationMatches) {
            return
        }

        setPurgeLoading(true)
        setPurgeError(undefined)

        try {
            const res = await helixSearchV2Api.purgeQueue({
                confirmation: purgeConfirmation,
            })
            toast.success(res.message || 'Helix search queue purge requested.')
            setShowPurgeModal(false)
            setPurgeConfirmation('')
            setRefreshToken((token) => token + 1)
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to purge Helix search queue.'
            setPurgeError(message)
            toast.error(message)
        } finally {
            setPurgeLoading(false)
        }
    }

    const handleOpenManualFailModal = (
        item: HelixSearchQueueItemDto,
        log: HelixSearchQueueLogDto,
    ) => {
        setManualFailTarget({
            sst: item.sst,
            tenantId: item.tenantId,
            logId: log.id,
            currentReceiptHandle: log.sqsReceiptHandle,
        })
        setManualFailReceiptHandle(log.sqsReceiptHandle ?? '')
        setManualFailSetSessionToFailed(true)
        setManualFailError(undefined)
    }

    const handleCloseManualFailModal = () => {
        if (manualFailLoading) {
            return
        }
        setManualFailTarget(undefined)
        setManualFailReceiptHandle('')
        setManualFailSetSessionToFailed(true)
        setManualFailError(undefined)
    }

    const handleManualFailReceiptHandleChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => {
        setManualFailReceiptHandle(event.target.value)
        if (manualFailError) {
            setManualFailError(undefined)
        }
    }

    const handleManualFailSetStatusChange = (checked: boolean) => {
        setManualFailSetSessionToFailed(checked)
    }

    const handleConfirmManualFail = async () => {
        if (!manualFailTarget || manualFailLoading) {
            return
        }

        setManualFailLoading(true)
        setManualFailError(undefined)

        const trimmedHandle = manualFailReceiptHandle.trim()

        try {
            const res = await helixSearchV2Api.manualFailLog({
                sst: manualFailTarget.sst,
                tenantId: manualFailTarget.tenantId,
                logId: manualFailTarget.logId,
                setSessionToFailed: manualFailSetSessionToFailed,
                sqsReceiptHandle: trimmedHandle ? trimmedHandle : undefined,
            })
            toast.success(
                res.message || 'Log manually marked as failed successfully.',
            )
            setManualFailTarget(undefined)
            setManualFailReceiptHandle('')
            setManualFailSetSessionToFailed(true)
            setRefreshToken((token) => token + 1)
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Failed to manually fail log item.'
            setManualFailError(message)
            toast.error(message)
        } finally {
            setManualFailLoading(false)
        }
    }

    const toggleRow = (sst: string) => {
        setExpandedRows((prev) => ({
            ...prev,
            [sst]: !prev[sst],
        }))
    }

    const perPage = queueMeta.perPage || PER_PAGE_DEFAULT
    const totalPages = useMemo(() => {
        if (queueMeta.total <= 0) return 1
        return Math.max(1, Math.ceil(queueMeta.total / perPage))
    }, [queueMeta.total, perPage])

    const canGoPrev = pageNumber > 1
    const hasNextPage =
        queueMeta.hasMore || pageNumber * perPage < queueMeta.total
    const canGoNext = hasNextPage

    const effectivePageNumber = Math.max(1, Math.min(pageNumber, totalPages))
    const visibleRangeStart =
        queueMeta.total === 0 ? 0 : (effectivePageNumber - 1) * perPage + 1
    const visibleRangeEndRaw =
        queueMeta.total === 0
            ? 0
            : (effectivePageNumber - 1) * perPage + items.length
    const visibleRangeEnd =
        queueMeta.total === 0
            ? 0
            : Math.max(visibleRangeStart, visibleRangeEndRaw)

    const summaryText =
        queueMeta.total === 0
            ? 'Showing 0 of 0'
            : `Showing ${visibleRangeStart.toLocaleString()} – ${visibleRangeEnd.toLocaleString()} of ${queueMeta.total.toLocaleString()}`

    const navigateToRole = (roleId: string | null) => {
        if (!roleId) {
            return
        }
        navigate(`${Pages.ManageRoleTA}?id=${encodeURIComponent(roleId)}`)
    }

    return (
        <div className="mt-6">
            <Section subtitle="Helix Search Queue">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        {STATUS_OPTIONS.map((option) => (
                            <CheckboxInput
                                key={option.value}
                                id={`helix-status-${option.value}`}
                                label={option.label}
                                checked={selectedStatuses.includes(
                                    option.value,
                                )}
                                onChange={(checked) =>
                                    handleToggleStatus(option.value, checked)
                                }
                                className="text-xs"
                            />
                        ))}
                        <CheckboxInput
                            id="helix-search-queue-auto-refresh"
                            label="Auto Refresh (5s)"
                            checked={autoRefresh}
                            onChange={handleAutoRefreshChange}
                            className="text-xs"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() =>
                                setRefreshToken((token) => token + 1)
                            }
                            enabled={!loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="button"
                            style="danger"
                            size="small"
                            onClick={handleOpenPurgeModal}
                            enabled={!loading}
                        >
                            Purge Queue
                        </Button>
                    </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                    <div>{summaryText}</div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => setPageNumber((value) => value - 1)}
                            enabled={canGoPrev && !loading}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {pageNumber} / {totalPages}
                        </span>
                        <Button
                            type="button"
                            style="secondary"
                            size="small"
                            onClick={() => setPageNumber((value) => value + 1)}
                            enabled={canGoNext && !loading}
                        >
                            Next
                        </Button>
                    </div>
                </div>
                {error ? (
                    <div className="mt-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                ) : null}
                <div className="relative mt-4">
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <LoadingSpinner captions={[]} spinnerSize={32} />
                        </div>
                    ) : null}
                    <div className="overflow-x-auto">
                        <table className={HELIX_TABLE_CLASS}>
                            <thead>
                                <tr className="uppercase text-[10px]">
                                    <th className={HELIX_HEADER_CELL_CLASS} />
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        SST
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Boolean Search Query
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Role ID
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Tenant ID
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Status
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Latest Log Status
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Network Searched
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Candidates Found
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Latest Log Processing Time
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Latest Log Total Time
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Error Message
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Created At
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Updated At
                                    </th>
                                    <th className={HELIX_HEADER_CELL_CLASS}>
                                        Created By
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && !loading ? (
                                    <tr>
                                        <td
                                            colSpan={HELIX_TABLE_COLUMN_COUNT}
                                            className="text-center py-4"
                                        >
                                            No Helix searches found.
                                        </td>
                                    </tr>
                                ) : null}
                                {items.map((item) => {
                                    const expanded = Boolean(
                                        expandedRows[item.sst],
                                    )
                                    const latestLog =
                                        item.logs.length > 0
                                            ? item.logs.reduce(
                                                  (latest, log) =>
                                                      log.updatedAt >
                                                      latest.updatedAt
                                                          ? log
                                                          : latest,
                                                  item.logs[0],
                                              )
                                            : undefined
                                    return (
                                        <Fragment key={item.sst}>
                                            <tr
                                                className={clsx(
                                                    expanded
                                                        ? 'bg-blue-50/40'
                                                        : undefined,
                                                )}
                                            >
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'w-12',
                                                    )}
                                                >
                                                    <button
                                                        type="button"
                                                        className="text-blue-600 underline"
                                                        onClick={() =>
                                                            toggleRow(item.sst)
                                                        }
                                                    >
                                                        {expanded
                                                            ? 'Hide'
                                                            : 'Logs'}
                                                    </button>
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'font-mono text-[10px] break-all',
                                                    )}
                                                >
                                                    {item.sst}
                                                </td>
                                                <td
                                                    className={HELIX_CELL_CLASS}
                                                >
                                                    <ExpandableTextBox
                                                        text={
                                                            item.booleanSearchQuery
                                                        }
                                                        maxLines={4}
                                                        className="min-w-0"
                                                        contentClassName="whitespace-pre-wrap break-words"
                                                    />
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'font-mono text-[10px] break-all',
                                                    )}
                                                >
                                                    {item.roleId ? (
                                                        <button
                                                            type="button"
                                                            className="text-blue-600 underline"
                                                            onClick={() =>
                                                                navigateToRole(
                                                                    item.roleId,
                                                                )
                                                            }
                                                        >
                                                            {item.roleId}
                                                        </button>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'font-mono text-[10px] break-all',
                                                    )}
                                                >
                                                    {item.tenantId}
                                                </td>
                                                <td
                                                    className={HELIX_CELL_CLASS}
                                                >
                                                    <HelixSearchSessionStatusBadge
                                                        status={item.status}
                                                    />
                                                </td>
                                                <td
                                                    className={HELIX_CELL_CLASS}
                                                >
                                                    {latestLog ? (
                                                        <HelixSearchLogStatusBadge
                                                            status={
                                                                latestLog.status
                                                            }
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'text-right',
                                                    )}
                                                >
                                                    {formatCount(
                                                        item.networkPersonCount,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'text-right',
                                                    )}
                                                >
                                                    {formatCount(
                                                        item.booleanSearchPersonCount,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'font-semibold text-right',
                                                        getDurationColorClass(
                                                            latestLog?.timeInProcessingMs,
                                                        ),
                                                    )}
                                                >
                                                    {latestLog
                                                        ? formatDurationMs(
                                                              latestLog.timeInProcessingMs,
                                                          )
                                                        : '—'}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'font-semibold text-right',
                                                        getTotalDurationColorClass(
                                                            latestLog?.timeTotalMs,
                                                        ),
                                                    )}
                                                >
                                                    {latestLog
                                                        ? formatDurationMs(
                                                              latestLog.timeTotalMs,
                                                          )
                                                        : '—'}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'max-w-[220px] text-[11px]',
                                                    )}
                                                >
                                                    <ExpandableTextBox
                                                        text={item.errMsg}
                                                        className="min-w-0"
                                                        contentClassName="whitespace-pre-wrap break-words"
                                                    />
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'text-center leading-tight',
                                                    )}
                                                >
                                                    {renderDateTime(
                                                        item.createdAt,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'text-center leading-tight',
                                                    )}
                                                >
                                                    {renderDateTime(
                                                        item.updatedAt,
                                                    )}
                                                </td>
                                                <td
                                                    className={clsx(
                                                        HELIX_CELL_CLASS,
                                                        'text-[11px] break-words',
                                                    )}
                                                >
                                                    {item.createdByEmail ??
                                                        item.createdBy ??
                                                        '—'}
                                                </td>
                                            </tr>
                                            {expanded ? (
                                                <tr className="bg-blue-50/20 text-[11px]">
                                                    <td
                                                        colSpan={
                                                            HELIX_TABLE_COLUMN_COUNT
                                                        }
                                                    >
                                                        <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 p-2">
                                                            <div className="mb-1 text-[11px] font-semibold text-blue-700 uppercase">
                                                                Log History
                                                            </div>
                                                            <table
                                                                className={
                                                                    HELIX_NESTED_TABLE_CLASS
                                                                }
                                                            >
                                                                <thead>
                                                                    <tr className="text-[10px] uppercase tracking-wide text-blue-800">
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Log
                                                                            ID
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Status
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Error
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Msg
                                                                            ID
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Receipt
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SQS
                                                                            Payload
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            SFN
                                                                            Exec
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Created
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Updated
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Queued
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Started
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Finished
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Wait
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Queue
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Processing
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Total
                                                                        </th>
                                                                        <th
                                                                            className={
                                                                                HELIX_HEADER_CELL_CLASS
                                                                            }
                                                                        >
                                                                            Actions
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.logs.map(
                                                                        (
                                                                            log,
                                                                        ) => (
                                                                            <tr
                                                                                key={
                                                                                    log.id
                                                                                }
                                                                            >
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'font-mono text-[10px] break-all',
                                                                                    )}
                                                                                >
                                                                                    {
                                                                                        log.id
                                                                                    }
                                                                                </td>
                                                                                <td
                                                                                    className={
                                                                                        HELIX_CELL_CLASS
                                                                                    }
                                                                                >
                                                                                    <HelixSearchLogStatusBadge
                                                                                        status={
                                                                                            log.status
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'max-w-[200px]',
                                                                                    )}
                                                                                >
                                                                                    <ExpandableTextBox
                                                                                        text={
                                                                                            log.errMsg
                                                                                        }
                                                                                        className="min-w-0"
                                                                                        contentClassName="whitespace-pre-wrap break-words"
                                                                                    />
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'font-mono text-[10px] break-all',
                                                                                    )}
                                                                                >
                                                                                    {log.sqsMsgId ??
                                                                                        '—'}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'max-w-[200px]',
                                                                                    )}
                                                                                >
                                                                                    <ExpandableTextBox
                                                                                        text={
                                                                                            log.sqsReceiptHandle
                                                                                        }
                                                                                        maxLines={
                                                                                            3
                                                                                        }
                                                                                        className="min-w-0"
                                                                                        contentClassName="break-all"
                                                                                    />
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'max-w-[200px]',
                                                                                    )}
                                                                                >
                                                                                    <ExpandableTextBox
                                                                                        text={formatJsonPayload(
                                                                                            log.sqsMsgPayload,
                                                                                        )}
                                                                                        maxLines={
                                                                                            3
                                                                                        }
                                                                                        className="min-w-0"
                                                                                    />
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'font-mono text-[10px] break-all',
                                                                                    )}
                                                                                >
                                                                                    {log.sfnExeName ? (
                                                                                        log.sfnExecutionUrl ? (
                                                                                            <a
                                                                                                href={
                                                                                                    log.sfnExecutionUrl
                                                                                                }
                                                                                                target="_blank"
                                                                                                rel="noreferrer"
                                                                                                className="text-blue-600 underline break-all"
                                                                                            >
                                                                                                {
                                                                                                    log.sfnExeName
                                                                                                }
                                                                                            </a>
                                                                                        ) : (
                                                                                            log.sfnExeName
                                                                                        )
                                                                                    ) : (
                                                                                        '—'
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center leading-tight',
                                                                                    )}
                                                                                >
                                                                                    {(() => {
                                                                                        const parts =
                                                                                            formatDateTimeParts(
                                                                                                log.createdAt,
                                                                                            )
                                                                                        return (
                                                                                            <>
                                                                                                <div>
                                                                                                    {
                                                                                                        parts.date
                                                                                                    }
                                                                                                </div>
                                                                                                <div className="text-[10px] text-gray-600">
                                                                                                    {
                                                                                                        parts.time
                                                                                                    }
                                                                                                </div>
                                                                                            </>
                                                                                        )
                                                                                    })()}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center leading-tight',
                                                                                    )}
                                                                                >
                                                                                    {renderDateTime(
                                                                                        log.updatedAt,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center leading-tight',
                                                                                    )}
                                                                                >
                                                                                    {renderDateTime(
                                                                                        log.queuedAt,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center leading-tight',
                                                                                    )}
                                                                                >
                                                                                    {renderDateTime(
                                                                                        log.startedAt,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center leading-tight',
                                                                                    )}
                                                                                >
                                                                                    {renderDateTime(
                                                                                        log.finishedAt,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-right',
                                                                                    )}
                                                                                >
                                                                                    {formatDurationMs(
                                                                                        log.timeWaitingToQueueMs,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-right',
                                                                                    )}
                                                                                >
                                                                                    {formatDurationMs(
                                                                                        log.timeInQueueMs,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-right',
                                                                                    )}
                                                                                >
                                                                                    {formatDurationMs(
                                                                                        log.timeInProcessingMs,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-right',
                                                                                    )}
                                                                                >
                                                                                    {formatDurationMs(
                                                                                        log.timeTotalMs,
                                                                                    )}
                                                                                </td>
                                                                                <td
                                                                                    className={clsx(
                                                                                        HELIX_CELL_CLASS,
                                                                                        'text-center',
                                                                                    )}
                                                                                >
                                                                                    {LOG_TERMINAL_STATUSES.has(
                                                                                        log.status,
                                                                                    ) ? (
                                                                                        '—'
                                                                                    ) : (
                                                                                        <Button
                                                                                            type="button"
                                                                                            style="danger"
                                                                                            size="small"
                                                                                            onClick={() =>
                                                                                                handleOpenManualFailModal(
                                                                                                    item,
                                                                                                    log,
                                                                                                )
                                                                                            }
                                                                                            enabled={
                                                                                                !manualFailLoading
                                                                                            }
                                                                                        >
                                                                                            Fail
                                                                                            Item
                                                                                        </Button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ),
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Section>
            {manualFailTarget ? (
                <Modal
                    title="Fail Helix Search Step"
                    onClose={handleCloseManualFailModal}
                    allowUserToClose={!manualFailLoading}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                size="small"
                                onClick={handleCloseManualFailModal}
                                enabled={!manualFailLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="danger"
                                size="small"
                                onClick={handleConfirmManualFail}
                                enabled={!manualFailLoading}
                            >
                                Mark Failed
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Provide an SQS receipt handle if you retrieved it
                            from AWS. Leaving it blank will reuse the last known
                            handle if we have one on record.
                        </p>
                        <div>
                            <label className="text-sm font-medium text-gray-700">
                                SQS Receipt Handle
                            </label>
                            <input
                                type="text"
                                value={manualFailReceiptHandle}
                                onChange={handleManualFailReceiptHandleChange}
                                disabled={manualFailLoading}
                                placeholder={
                                    manualFailTarget.currentReceiptHandle
                                        ? 'Leave blank to use recorded handle'
                                        : 'Paste the handle if available'
                                }
                                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <span className="text-xs text-gray-500 break-all">
                                {manualFailTarget.currentReceiptHandle
                                    ? `Recorded handle: ${manualFailTarget.currentReceiptHandle}`
                                    : 'Provide a handle if you retrieved it from AWS.'}
                            </span>
                        </div>
                        <CheckboxInput
                            id="helix-manual-fail-set-session"
                            label="Also mark the search session as FAILED"
                            checked={manualFailSetSessionToFailed}
                            onChange={handleManualFailSetStatusChange}
                            disabled={manualFailLoading}
                            description="Keeps the session row in sync with the log entry. Disable only if you need to preserve the current session status."
                        />
                        {manualFailError ? (
                            <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {manualFailError}
                            </div>
                        ) : null}
                    </div>
                </Modal>
            ) : null}
            {showPurgeModal ? (
                <Modal
                    title="Purge Helix Search Queue"
                    onClose={handleClosePurgeModal}
                    allowUserToClose={!purgeLoading}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                size="small"
                                onClick={handleClosePurgeModal}
                                enabled={!purgeLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="danger"
                                size="small"
                                onClick={handleConfirmPurge}
                                enabled={
                                    purgeConfirmationMatches && !purgeLoading
                                }
                            >
                                Purge Queue
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                            Purging the queue will delete all in-flight and
                            pending Helix search requests from SQS. Searches in
                            progress may fail and will need to be retriggered.
                        </p>
                        <p className="text-sm text-gray-700">
                            To confirm, type{' '}
                            <span className="font-semibold">
                                {PURGE_CONFIRMATION_TEXT}
                            </span>{' '}
                            exactly (case sensitive) in the field below, then
                            press Purge Queue.
                        </p>
                        <input
                            type="text"
                            value={purgeConfirmation}
                            onChange={handlePurgeConfirmationChange}
                            autoFocus
                            disabled={purgeLoading}
                            placeholder={PURGE_CONFIRMATION_TEXT}
                            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        />
                        <p className="text-xs text-gray-500">
                            AWS only allows a purge request once every 60
                            seconds. If a purge was triggered recently, retrying
                            too soon will fail.
                        </p>
                        {purgeError ? (
                            <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {purgeError}
                            </div>
                        ) : null}
                    </div>
                </Modal>
            ) : null}
        </div>
    )
}

function getDurationColorClass(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return 'text-gray-600'
    }

    if (value <= 60_000) {
        return 'text-green-700'
    }

    if (value <= 300_000) {
        return 'text-yellow-700'
    }

    return 'text-red-700'
}

function getTotalDurationColorClass(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return 'text-gray-600'
    }

    if (value > 900_000) {
        return 'text-red-700'
    }

    return 'text-gray-600'
}
